import React, { useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { updateOrder, deleteOrder } from '../../store/slices/ordersSlice';
import { OrderCard } from '../orders/components/OrderCard';
import { Order } from '../../types/order';
import { CalendarEvent } from '../../types/calendar';
import { Recipe } from '../../types/recipe';
import { getDaysSinceOrder, getOrderRowBackgroundColor } from '../../utils/orderUtils';

export const ClientDetailPage: React.FC = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const clients = useAppSelector(state => state.clients.clients);
  const activeOrders = useAppSelector(state => state.orders.orders);
  const completedOrders = useAppSelector(state => state.orders.completedOrders);
  const calendarEvents = useAppSelector(state => state.calendar.events);
  const fillings = useAppSelector(state => state.recipes.fillings);
  const [isOrderHistoryExpanded, setIsOrderHistoryExpanded] = useState(false);
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);
  const orderRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const client = clients.find(c => c.id === clientId);

  // Filter active orders for this client (only Pending or In Progress)
  const clientActiveOrders = activeOrders.filter(
    order => order.clientId === clientId && (order.status === 'Pending' || order.status === 'In Progress')
  );

  // Sort active orders by order date (oldest first to show most urgent)
  const sortedActiveOrders = [...clientActiveOrders].sort((a, b) => {
    return new Date(a.orderDate).getTime() - new Date(b.orderDate).getTime();
  });

  // Filter completed orders for this client
  const clientCompletedOrders = completedOrders.filter(order => order.clientId === clientId);

  // Sort completed orders by completion date (most recently completed first)
  const sortedClientOrders = [...clientCompletedOrders].sort((a, b) => {
    const eventA = calendarEvents.find((event: CalendarEvent) => event.orderId === a.id && event.completed);
    const eventB = calendarEvents.find((event: CalendarEvent) => event.orderId === b.id && event.completed);
    const timeA = eventA?.updatedAt ? new Date(eventA.updatedAt).getTime() : new Date(a.orderDate).getTime();
    const timeB = eventB?.updatedAt ? new Date(eventB.updatedAt).getTime() : new Date(b.orderDate).getTime();
    return timeB - timeA;
  });

  // Calculate flavor summary for this client's completed orders
  const flavorSummary = React.useMemo(() => {
    const summary: Record<string, number> = {};

    clientCompletedOrders.forEach(order => {
      order.items.forEach(item => {
        const flavorName = item.recipeName;
        if (flavorName) {
          summary[flavorName] = (summary[flavorName] || 0) + item.quantity;
        }
      });
    });

    // Convert to sorted array for display
    return Object.entries(summary)
      .sort(([nameA], [nameB]) => nameA.localeCompare(nameB))
      .map(([name, count]) => ({ name, count }));
  }, [clientCompletedOrders]);

  // Recipe options for order cards
  const recipeOptions = fillings.map((r: Recipe) => ({
    id: r.id,
    name: r.name
  }));

  const handleUpdateOrder = (updatedOrder: any) => {
    dispatch(updateOrder(updatedOrder));
  };

  const handleDeleteOrder = (id: string) => {
    if (window.confirm('Are you sure you want to delete this order?')) {
      dispatch(deleteOrder(id));
    }
  };

  const handleToggleOrder = (orderId: string) => {
    setExpandedOrderId(prevId => (prevId === orderId ? null : orderId));
  };

  if (!client) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          padding: '20px'
        }}
      >
        <h1 style={{ fontSize: '2rem', color: '#666', marginBottom: '16px' }}>Client Not Found</h1>
        <p style={{ color: '#999', marginBottom: '24px' }}>
          The client you're looking for doesn't exist or has been removed.
        </p>
        <Link
          to="/clients"
          style={{
            padding: '12px 24px',
            backgroundColor: '#4CAF50',
            color: 'white',
            textDecoration: 'none',
            borderRadius: '4px',
            fontWeight: 'bold'
          }}
        >
          Back to Clients List
        </Link>
      </div>
    );
  }

  const createdDate = new Date(client.createdAt).toLocaleDateString();

  return (
    <div
      style={{
        maxWidth: '800px',
        margin: '0 auto',
        padding: '40px 20px'
      }}
    >
      {/* Back Button */}
      <Link
        to="/clients"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          color: '#4CAF50',
          textDecoration: 'none',
          marginBottom: '24px',
          fontSize: '1rem',
          fontWeight: 'bold'
        }}
      >
        <span>←</span>
        <span>Back to Clients</span>
      </Link>

      {/* Client Header */}
      <div
        style={{
          backgroundColor: client.type === 'retailer' ? '#e3f2fd' : '#f5f5f5',
          padding: '24px',
          borderRadius: '8px',
          marginBottom: '24px'
        }}
      >
        {client.type === 'individual' ? (
          <h1 style={{ margin: '0 0 8px 0', fontSize: '2rem' }}>{client.name || 'Unnamed Client'}</h1>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
              <h1 style={{ margin: 0, fontSize: '2rem' }}>{client.retailerName || 'Unnamed Retailer'}</h1>
              <span
                style={{
                  fontSize: '0.75rem',
                  color: '#2196F3',
                  backgroundColor: 'white',
                  padding: '4px 12px',
                  borderRadius: '4px',
                  fontWeight: 'bold'
                }}
              >
                RETAILER
              </span>
            </div>
            <p style={{ margin: 0, fontSize: '1.1rem', color: '#555' }}>
              Contact: {client.contactName || 'No contact name'}
            </p>
          </>
        )}
        <p style={{ margin: '8px 0 0 0', color: '#666' }}>Added on {createdDate}</p>
      </div>

      {/* Client Details */}
      <div
        style={{
          backgroundColor: 'white',
          border: '1px solid #ddd',
          borderRadius: '8px',
          padding: '24px'
        }}
      >
        <h2 style={{ marginTop: 0, marginBottom: '20px', fontSize: '1.5rem' }}>Contact Information</h2>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '20px',
            marginBottom: '24px'
          }}
        >
          <div>
            <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px', color: '#555' }}>Email</label>
            <p style={{ margin: 0, fontSize: '1rem' }}>{client.email || 'No email provided'}</p>
          </div>

          <div>
            <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px', color: '#555' }}>Phone</label>
            <p style={{ margin: 0, fontSize: '1rem' }}>{client.phone || 'No phone provided'}</p>
          </div>

          <div style={{ gridColumn: 'span 2' }}>
            <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px', color: '#555' }}>Address</label>
            <p style={{ margin: 0, fontSize: '1rem' }}>{client.address || 'No address provided'}</p>
          </div>
        </div>

        <h2 style={{ marginTop: '32px', marginBottom: '20px', fontSize: '1.5rem' }}>Additional Information</h2>

        <div style={{ marginBottom: '24px' }}>
          <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px', color: '#555' }}>
            Preferences
          </label>
          <p
            style={{
              margin: 0,
              fontSize: '1rem',
              whiteSpace: 'pre-wrap',
              backgroundColor: '#f5f5f5',
              padding: client.preferences ? '12px' : '12px',
              borderRadius: '4px',
              minHeight: '20px'
            }}
          >
            {client.preferences || 'No preferences listed'}
          </p>
        </div>

        <div>
          <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px', color: '#555' }}>Notes</label>
          <p
            style={{
              margin: 0,
              fontSize: '1rem',
              whiteSpace: 'pre-wrap',
              backgroundColor: '#f5f5f5',
              padding: client.notes ? '12px' : '12px',
              borderRadius: '4px',
              minHeight: '20px'
            }}
          >
            {client.notes || 'No notes'}
          </p>
        </div>
      </div>

      {/* Order Summary Section */}
      {flavorSummary.length > 0 && (
        <div
          style={{
            backgroundColor: '#f9f9f9',
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            padding: '20px',
            marginTop: '24px'
          }}
        >
          <h2 style={{ marginTop: 0, marginBottom: '16px', fontSize: '1.5rem', color: '#333' }}>
            Order Summary - Units by Flavor
          </h2>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
              gap: '12px'
            }}
          >
            {flavorSummary.map(({ name, count }) => (
              <div
                key={name}
                style={{
                  backgroundColor: 'white',
                  borderRadius: '4px',
                  border: '1px solid #ddd',
                  padding: '12px 16px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <span
                  style={{
                    fontWeight: '500',
                    color: '#333',
                    fontSize: '0.95rem'
                  }}
                >
                  {name}
                </span>
                <span
                  style={{
                    fontWeight: 'bold',
                    color: '#4CAF50',
                    fontSize: '1.1rem',
                    marginLeft: '12px'
                  }}
                >
                  {count} {count === 1 ? 'unit' : 'units'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Active Orders Section */}
      {sortedActiveOrders.length > 0 && (
        <div
          style={{
            backgroundColor: 'white',
            border: '2px solid #2196F3',
            borderRadius: '8px',
            marginTop: '24px',
            overflow: 'hidden'
          }}
        >
          <div
            style={{
              padding: '20px 24px',
              backgroundColor: '#e3f2fd',
              borderBottom: '2px solid #2196F3'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <h2 style={{ margin: 0, fontSize: '1.5rem', color: '#1976D2' }}>Active Orders</h2>
              <span
                style={{
                  padding: '4px 12px',
                  backgroundColor: '#2196F3',
                  color: 'white',
                  borderRadius: '12px',
                  fontSize: '0.875rem',
                  fontWeight: 600
                }}
              >
                {sortedActiveOrders.length} {sortedActiveOrders.length === 1 ? 'order' : 'orders'}
              </span>
            </div>
          </div>

          <div style={{ padding: '24px' }}>
            <div
              style={{
                display: 'grid',
                gap: '16px'
              }}
            >
              {sortedActiveOrders.map(order => {
                const daysSince = getDaysSinceOrder(order.orderDate);
                const deliveryEvent = calendarEvents.find((event: CalendarEvent) => event.orderId === order.id);
                const totalPrice = order.pricing?.total;

                return (
                  <div
                    key={order.id}
                    style={{
                      border: '2px solid #ddd',
                      borderRadius: '8px',
                      padding: '20px',
                      backgroundColor: getOrderRowBackgroundColor(daysSince, order.status),
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}
                  >
                    {/* Order Header */}
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginBottom: '16px',
                        paddingBottom: '12px',
                        borderBottom: '1px solid #e0e0e0'
                      }}
                    >
                      <div>
                        <h3 style={{ margin: '0 0 8px 0', fontSize: '1.2rem', color: '#333' }}>
                          Order #{order.id.substring(0, 8)}
                        </h3>
                        <div style={{ fontSize: '0.9rem', color: '#666' }}>
                          <span style={{ fontWeight: 'bold' }}>Order Date:</span> {order.orderDate}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div
                          style={{
                            padding: '6px 12px',
                            borderRadius: '4px',
                            backgroundColor:
                              order.status === 'Pending'
                                ? '#FF9800'
                                : order.status === 'In Progress'
                                  ? '#2196F3'
                                  : '#4CAF50',
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '0.875rem',
                            marginBottom: '8px'
                          }}
                        >
                          {order.status}
                        </div>
                        <div
                          style={{
                            padding: '4px 8px',
                            borderRadius: '4px',
                            backgroundColor:
                              daysSince <= 2
                                ? '#4CAF50'
                                : daysSince <= 4
                                  ? '#FFC107'
                                  : daysSince <= 6
                                    ? '#FF9800'
                                    : '#F44336',
                            color: 'white',
                            fontSize: '0.75rem',
                            fontWeight: 'bold'
                          }}
                        >
                          {daysSince} day{daysSince !== 1 ? 's' : ''} old
                        </div>
                      </div>
                    </div>

                    {/* Order Items */}
                    <div style={{ marginBottom: '16px' }}>
                      <h4 style={{ margin: '0 0 12px 0', fontSize: '1rem', color: '#555' }}>Items:</h4>
                      <div
                        style={{
                          backgroundColor: 'rgba(255, 255, 255, 0.5)',
                          borderRadius: '4px',
                          padding: '12px'
                        }}
                      >
                        {order.items.map((item, idx) => (
                          <div
                            key={item.id}
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              padding: '8px 0',
                              borderBottom: idx < order.items.length - 1 ? '1px solid #e0e0e0' : 'none'
                            }}
                          >
                            <span style={{ fontWeight: '500', color: '#333' }}>{item.recipeName}</span>
                            <span style={{ fontWeight: 'bold', color: '#2196F3' }}>
                              {item.quantity} {item.quantity === 1 ? 'unit' : 'units'}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Delivery Date and Price */}
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: deliveryEvent ? '1fr 1fr' : '1fr',
                        gap: '16px',
                        paddingTop: '12px',
                        borderTop: '1px solid #e0e0e0'
                      }}
                    >
                      {deliveryEvent && (
                        <div>
                          <span style={{ fontSize: '0.85rem', color: '#666', display: 'block', marginBottom: '4px' }}>
                            Delivery Date:
                          </span>
                          <span style={{ fontSize: '1rem', fontWeight: 'bold', color: '#1976D2' }}>
                            {deliveryEvent.startDate}
                          </span>
                        </div>
                      )}
                      {totalPrice !== undefined && (
                        <div style={{ textAlign: deliveryEvent ? 'right' : 'left' }}>
                          <span style={{ fontSize: '0.85rem', color: '#666', display: 'block', marginBottom: '4px' }}>
                            Total Price:
                          </span>
                          <span style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#4CAF50' }}>
                            ${totalPrice.toFixed(2)}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Badges */}
                    <div style={{ marginTop: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {!order.isFinalized && (
                        <span
                          style={{
                            fontSize: '0.75rem',
                            padding: '4px 8px',
                            backgroundColor: '#FFE082',
                            color: '#F57C00',
                            borderRadius: '4px',
                            fontWeight: 'bold'
                          }}
                        >
                          DRAFT
                        </span>
                      )}
                      {order.isReady && (
                        <span
                          style={{
                            fontSize: '0.75rem',
                            padding: '4px 8px',
                            backgroundColor: '#4CAF50',
                            color: 'white',
                            borderRadius: '4px',
                            fontWeight: 'bold'
                          }}
                        >
                          READY
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Order History Section */}
      <div
        style={{
          backgroundColor: 'white',
          border: '1px solid #ddd',
          borderRadius: '8px',
          marginTop: '24px',
          overflow: 'hidden'
        }}
      >
        {/* Order History Header - Collapsible */}
        <div
          onClick={() => setIsOrderHistoryExpanded(!isOrderHistoryExpanded)}
          style={{
            padding: '20px 24px',
            backgroundColor: '#f8f9fa',
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            transition: 'background-color 0.2s',
            userSelect: 'none'
          }}
          onMouseEnter={e => {
            e.currentTarget.style.backgroundColor = '#e9ecef';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.backgroundColor = '#f8f9fa';
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <h2 style={{ margin: 0, fontSize: '1.5rem' }}>Order History</h2>
            <span
              style={{
                padding: '4px 12px',
                backgroundColor: sortedClientOrders.length > 0 ? '#4CAF50' : '#999',
                color: 'white',
                borderRadius: '12px',
                fontSize: '0.875rem',
                fontWeight: 600
              }}
            >
              {sortedClientOrders.length} {sortedClientOrders.length === 1 ? 'order' : 'orders'}
            </span>
          </div>
          <span
            style={{
              fontSize: '1.5rem',
              color: '#6c757d',
              transition: 'transform 0.2s',
              transform: isOrderHistoryExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
            }}
          >
            ▼
          </span>
        </div>

        {/* Order History Content - Collapsible */}
        {isOrderHistoryExpanded && (
          <div style={{ padding: '24px' }}>
            {sortedClientOrders.length === 0 ? (
              <div
                style={{
                  textAlign: 'center',
                  padding: '40px 20px',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '8px',
                  color: '#999'
                }}
              >
                <p style={{ fontSize: '1.1rem', margin: '0 0 8px 0' }}>No completed orders yet</p>
                <p style={{ margin: 0 }}>Orders will appear here once they are completed</p>
              </div>
            ) : (
              <div>
                {sortedClientOrders.map(order => (
                  <OrderCard
                    key={order.id}
                    ref={el => {
                      orderRefs.current[order.id] = el;
                    }}
                    order={order}
                    recipes={recipeOptions}
                    onUpdate={handleUpdateOrder}
                    onDelete={handleDeleteOrder}
                    forceExpanded={expandedOrderId === order.id}
                    onToggle={() => handleToggleOrder(order.id)}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
